/**
 * StatCard Component
 *
 * Professional stat card component for dashboards with:
 * - Large primary metric display
 * - Optional trend indicator (percentage change)
 * - Icon support
 * - Subtitle/description support
 * - Action button support
 * - Multiple size variants
 *
 * Usage:
 * <StatCard
 *   title="Total Applications"
 *   value={45}
 *   trend={{ value: 12, direction: 'up' }}
 *   icon={<ClipboardList className="w-6 h-6" />}
 *   subtitle="3 pending review"
 * />
 */

import type { ReactElement, ReactNode } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';

export interface StatCardProps {
  /** Main title/label for the stat */
  title: string;

  /** Primary value to display (number or string) */
  value: string | number;

  /** Optional trend data */
  trend?: {
    value: number;
    direction: 'up' | 'down';
    label?: string;
  };

  /** Optional icon element */
  icon?: ReactElement;

  /** Optional subtitle/description */
  subtitle?: string;

  /** Optional action button */
  action?: {
    label: string;
    onClick: () => void;
  };

  /** Card variant */
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'error';

  /** Card size */
  size?: 'sm' | 'md' | 'lg';

  /** Optional loading state */
  loading?: boolean;

  /** Custom className */
  className?: string;

  /** Optional children for custom content */
  children?: ReactNode;
}

export function StatCard({
  title,
  value,
  trend,
  icon,
  subtitle,
  action,
  variant = 'default',
  size = 'md',
  loading = false,
  className = '',
  children,
}: StatCardProps) {
  const variantStyles = {
    default: {
      bg: 'bg-white',
      border: 'border-neutral-200',
      iconBg: 'bg-neutral-100',
      iconColor: 'text-neutral-600',
      valueColor: 'text-neutral-900',
    },
    primary: {
      bg: 'bg-primary-50',
      border: 'border-primary-200',
      iconBg: 'bg-primary-100',
      iconColor: 'text-primary-600',
      valueColor: 'text-primary-900',
    },
    success: {
      bg: 'bg-success-50',
      border: 'border-success-200',
      iconBg: 'bg-success-100',
      iconColor: 'text-success-600',
      valueColor: 'text-success-900',
    },
    warning: {
      bg: 'bg-warning-50',
      border: 'border-warning-200',
      iconBg: 'bg-warning-100',
      iconColor: 'text-warning-600',
      valueColor: 'text-warning-900',
    },
    error: {
      bg: 'bg-error-50',
      border: 'border-error-200',
      iconBg: 'bg-error-100',
      iconColor: 'text-error-600',
      valueColor: 'text-error-900',
    },
  };

  const sizeStyles = {
    sm: {
      padding: 'p-4',
      valueSize: 'text-2xl',
      titleSize: 'text-sm',
    },
    md: {
      padding: 'p-6',
      valueSize: 'text-3xl',
      titleSize: 'text-base',
    },
    lg: {
      padding: 'p-8',
      valueSize: 'text-4xl',
      titleSize: 'text-lg',
    },
  };

  const styles = variantStyles[variant];
  const sizes = sizeStyles[size];

  const getTrendColor = () => {
    if (!trend) return '';
    return trend.direction === 'up' ? 'text-success-600' : 'text-error-600';
  };

  const TrendIcon = trend?.direction === 'up' ? TrendingUp : TrendingDown;

  if (loading) {
    return (
      <div className={`${styles.bg} border ${styles.border} rounded-lg ${sizes.padding} ${className} animate-pulse`}>
        <div className="flex items-start justify-between mb-4">
          <div className="h-4 bg-neutral-200 rounded w-24"></div>
          {icon && <div className="h-10 w-10 bg-neutral-200 rounded-lg"></div>}
        </div>
        <div className="h-8 bg-neutral-200 rounded w-16 mb-2"></div>
        <div className="h-3 bg-neutral-200 rounded w-32"></div>
      </div>
    );
  }

  return (
    <div
      className={`
        ${styles.bg} border ${styles.border} rounded-lg ${sizes.padding}
        transition-all hover:shadow-md ${className}
      `}
    >
      {/* Header: Title and Icon */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className={`${sizes.titleSize} font-medium text-neutral-700`}>{title}</h3>
        </div>
        {icon && (
          <div className={`${styles.iconBg} ${styles.iconColor} p-2 rounded-lg shrink-0 ml-2`}>
            {icon}
          </div>
        )}
      </div>

      {/* Primary Value */}
      <div className="flex items-baseline gap-3 mb-2">
        <span className={`${sizes.valueSize} font-bold ${styles.valueColor}`}>
          {value.toLocaleString()}
        </span>

        {/* Trend Indicator */}
        {trend && (
          <div className={`flex items-center gap-1 text-sm font-medium ${getTrendColor()}`}>
            <TrendIcon className="w-4 h-4" />
            <span>{trend.value}%</span>
            {trend.label && <span className="text-neutral-500 text-xs ml-1">{trend.label}</span>}
          </div>
        )}
      </div>

      {/* Subtitle */}
      {subtitle && (
        <p className="text-sm text-neutral-600 mb-3">{subtitle}</p>
      )}

      {/* Custom Children */}
      {children && (
        <div className="mt-3">
          {children}
        </div>
      )}

      {/* Action Button */}
      {action && (
        <button
          onClick={action.onClick}
          className="mt-3 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
        >
          {action.label} →
        </button>
      )}
    </div>
  );
}

export default StatCard;
